import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { BaseComponent } from '../../../common/components/base.component';
import {
  EditorChoiceDataSource,
  HubEvent,
} from '../../../models/hub/hub.event';
import { VisitorApiService } from '../../../services/visitor.api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { VisitorCalendarActionCommand } from '../../../commands/pagination.query.command';
import { EventTimeService } from '../../../services/event.time.service';
import { localStorageKeys } from '../../../common/constants/local.storage.keys';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VisitorCalendarStore } from '../../../stores/visitor.calendar.store';
import { AccordionComponent } from '../../../common/components/accordion/accordion.component';
import { DateFieldComponent } from '../../../common/components/date.time.field/date.field.component';
import { CustomEventCommand } from '../../../commands/hub/custom.event.command';
import { EditorChoiceStore } from '../../../stores/hub.calendar.store';
import {
  getRouteUrl,
  routeConfigs,
} from '../../../common/constants/route.config';
import { clone } from 'common/extensions/object/object.extensions';

@Component({
  selector: 'ig-event-filter',
  templateUrl: './event.filter.component.html',
  styleUrls: ['./event.filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EventFilterComponent
  extends BaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public hubName: string;
  public customEventBtnId: string;
  public dateFormGroup: FormGroup;
  public from: Date = null;
  public to: Date = null;
  public fromForSelector: Date;
  public toForSelector: Date;
  public selectedEvents: string[] = [];
  public isFilteringBySpecificDates = false;
  public selectedCustomEvent = new CustomEventCommand();
  public command = new VisitorCalendarActionCommand();
  public editorChoiceText: string;

  @Input() mode: 'topbar' | 'sidebar' | 'map' = 'topbar';
  @Input() customEvents: CustomEventCommand[];
  @Input() currentEvent: HubEvent;
  @Input() isHideEditorChoice = false;
  @Output() public onFilter = new EventEmitter<any>();

  @ViewChild('customEventAccordion')
  public customEventAccordion: AccordionComponent;
  @ViewChild('eventDateAccordion')
  public eventDateAccordion: AccordionComponent;
  @ViewChild('from') public fromDateField: DateFieldComponent;
  @ViewChild('to') public toDateField: DateFieldComponent;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private visitorApiService: VisitorApiService,
    private formBuilder: FormBuilder,
    private visitorCalendarStore: VisitorCalendarStore,
    private editorChoiceStore: EditorChoiceStore
  ) {
    super({ activatedRoute });
  }

  ngOnInit(): void {
    let { hubName, customEventBtnId } = this.allRouteParams;
    this.hubName = hubName;
    this.setSelectedCustomEventBtn(customEventBtnId);

    this.dateFormGroup = this.formBuilder.group({
      from: [null, [Validators.required]],
      to: [null, [Validators.required]],
    });

    this.editorChoiceTextHandler();

    this.visitorCalendarStore.actionCommand
      .takeUntil(this.isDestroyed)
      .subscribe((command) => {
        if (!command) return;

        setTimeout(() => {
          this.command = clone(command);
          if (command.queryCommand.dateFilterText) {
            this.eventDateAccordion.setFilter(
              command.queryCommand.dateFilterText.toUpperCase(),
              command.queryCommand.dateFilterText !== 'FILTER BY DATE'
            );
          }
          if (command.queryCommand.customEventBtnText) {
            this.customEventAccordion.setFilter(
              command.queryCommand.customEventBtnText,
              true
            );
            const customBtn = this.customEvents.find(
              (x) => x.btnText === command.queryCommand.customEventBtnText
            );
            if (customBtn) {
              this.selectedCustomEvent = customBtn;
            }
          } else {
            this.selectedCustomEvent = null;
          }
          if (command.queryCommand.choice) {
            this.customEventAccordion.setFilter(this.editorChoiceText, true);
          }
        });
      });
  }

  public onDateFieldKeyPress($event, type) {
    if ($event && $event.key === 'Enter') {
      type === 'from'
        ? this.fromDateField.onKeyPress($event)
        : this.toDateField.onKeyPress($event);
    }
  }

  public clearFilter() {
    this.eventDateAccordion && this.eventDateAccordion.clearFilter(null, false);
    this.customEventAccordion &&
      this.customEventAccordion.clearFilter(null, false);
  }

  public ngAfterViewInit() {
    if (this.mode === 'topbar') {
      window.addEventListener('click', (target) => {
        if (
          target &&
          target.srcElement &&
          (target.srcElement['className'].indexOf('md2-datepicker') >= 0 ||
            target.srcElement['className'].indexOf('md2-calendar') >= 0 ||
            target.srcElement['className'].indexOf('ng-tns') >= 0 ||
            target.srcElement['className'].indexOf('selected') >= 0 ||
            target.srcElement['className'].indexOf('value') >= 0)
        ) {
          return;
        }

        this.closeAccordion();
      });
    }
  }

  public ngOnDestroy() {
    window.removeEventListener('click', () => {
      this.closeAccordion();
    });
  }

  public filterEvents($event, title) {
    if ($event) {
      $event.stopPropagation();
    }
    let queryParams = title || 'defaultTarget';

    if (title === 'showSpecificDates') {
      this.isFilteringBySpecificDates = true;
      this.command.queryCommand.dateFilterText = 'Specific Dates';
      return;
    } else if (title !== 'Specific Dates') {
      this.isFilteringBySpecificDates = false;
    }
    this.onFilter.emit();

    let filterText = queryParams;

    let lastMid = new Date(new Date().setHours(0, 0, 0, 0));
    let nextMid = new Date(new Date().setHours(24, 0, 0, 0));
    switch (queryParams) {
      case 'Today':
        this.from = lastMid;
        this.to = nextMid;
        this.fromForSelector = null;
        this.toForSelector = null;
        break;
      case 'Tomorrow':
        this.from = new Date(lastMid.setDate(lastMid.getDate() + 1));
        this.to = new Date(lastMid.setDate(nextMid.getDate() + 1));
        this.fromForSelector = null;
        this.toForSelector = null;
        break;
      case 'This Weekend':
        // from Friday to Sunday
        this.from = new Date(
          lastMid.setDate(lastMid.getDate() + (7 - lastMid.getDay() - 2))
        );
        this.to = new Date(
          nextMid.setDate(nextMid.getDate() + (7 - nextMid.getDay() + 1))
        );
        this.fromForSelector = null;
        this.toForSelector = null;
        break;
      case 'This Week':
        this.from = new Date(
          lastMid.setDate(lastMid.getDate() + (lastMid.getDay() - 2))
        );
        this.to = new Date(
          nextMid.setDate(nextMid.getDate() + (7 - nextMid.getDay()))
        );
        this.fromForSelector = null;
        this.toForSelector = null;
        break;
      case 'Specific Dates':
        if (this.fromForSelector) {
          this.from = this.fromForSelector;
          this.from.setHours(0);
          this.from.setMinutes(0);
          this.from.setSeconds(0);
          const timePrefix = this.toForSelector ? '' : 'After ';
          filterText =
            timePrefix + EventTimeService.toDayAndMonthFormat(this.from);
        } else {
          this.from = null;
          filterText = 'Before';
        }
        if (this.toForSelector) {
          this.to = this.toForSelector;
          this.to.setHours(23);
          this.to.setMinutes(59);
          this.to.setSeconds(59);
          filterText += ' - ' + EventTimeService.toDayAndMonthFormat(this.to);
        } else {
          this.to = null;
        }
        break;
      case 'allDates':
        this.from = null;
        this.to = null;
        this.fromForSelector = null;
        this.toForSelector = null;
        filterText = 'FILTER BY DATE';

      // this.gotoEventCalendar();
      // return;
      case 'defaultTarget':
        break;
      case 'search':
        break;
      default:
      // this.category = queryParams;
      // command.isEventCalendar = true;
    }

    this.command.queryCommand.search = null;
    this.command.queryCommand.from = this.from || null;
    this.command.queryCommand.to = this.to || null;
    this.command.queryCommand.category = 'all'; //this.category || null;
    this.command.queryCommand.joinedEventsIds = null;
    this.command.queryCommand.feedId = null;
    this.command.queryCommand.dateFilterText = filterText;

    // if (!this.command.isEventCalendar) {
    //     command.queryCommand.from = null;
    //     command.queryCommand.to = null;
    // }

    this.visitorCalendarStore.actionCommand.next(this.command);
    // visitor.components.ts uses this to handle browser's back button event
    console.log('topbar filterEvents command', this.command);
    localStorage.setItem(
      localStorageKeys.eventQueryCommand,
      JSON.stringify(this.command)
    );
    this.mode === 'topbar' && this.closeAccordion();
    this.eventDateAccordion.setFilter(
      filterText.toUpperCase(),
      queryParams !== 'allDates'
    );
    // this.customEventAccordion && this.customEventAccordion.clearFilter(null, false);

    // topbar and sidebar need to load calendar if on create event view
    if (this.mode !== 'map') {
      this.router.navigateByUrl(
        getRouteUrl(routeConfigs.visitor.eventCalendar, {
          hubName: this.hubName,
        }),
        { skipLocationChange: this.noChangeUrlWhenRedirect }
      );
    }
  }

  public getFeedEvent(custom: any) {
    console.log('custom', custom);
    this.onFilter.emit();

    this.command.queryCommand.choice = null;
    this.command.queryCommand.search = null;
    // this.command.queryCommand.from = this.from;
    // this.command.queryCommand.to = this.to;
    this.command.queryCommand.category = null;
    this.command.queryCommand.joinedEventsIds = null;
    this.command.queryCommand.isCustom =
      custom.sourceType === 'selected' ? true : null;
    this.command.queryCommand.selectedEvents =
      custom.sourceType === 'selected' ? custom.events : null;
    this.command.queryCommand.customEventBtnText = custom.btnText;
    this.command.queryCommand.feedId =
      custom.sourceType === 'feed' ? custom.feedId : null;
    this.command.isEventCalendar = true;
    console.log('topbar getFeedEvent command', this.command);
    this.visitorCalendarStore.actionCommand.next(this.command);
    this.selectedCustomEvent = null;
    // visitor.components.ts uses this to handle browser's back button event
    localStorage.setItem(
      localStorageKeys.eventQueryCommand,
      JSON.stringify(this.command)
    );

    this.customEventAccordion.setFilter(
      custom.btnText || 'EVENT CATEGORIES',
      custom.sourceType.toLowerCase() !== 'all'
    );
    this.mode === 'topbar' && this.closeAccordion();

    if (this.mode !== 'map') {
      this.router.navigateByUrl(
        getRouteUrl(routeConfigs.visitor.eventCalendar, {
          hubName: this.hubName,
        }),
        { skipLocationChange: this.noChangeUrlWhenRedirect }
      );
    }
  }

  public editChoice() {
    this.onFilter.emit();
    this.command.queryCommand.search = null;
    // command.queryCommand.from = null;
    // command.queryCommand.to = null;
    this.command.queryCommand.category = null;
    this.command.queryCommand.choice = true;
    this.command.queryCommand.isCustom = null;
    this.command.queryCommand.mostAttended = null;
    this.command.queryCommand.feedId = null;
    this.command.queryCommand.joinedEventsIds = null;
    this.command.queryCommand.selectedEvents = null;
    this.command.queryCommand.customEventBtnText = null;
    this.visitorCalendarStore.actionCommand.next(this.command);
    this.selectedCustomEvent = null;

    // visitor.components.ts uses this to handle browser's back button event
    console.log('topbar editor choice command', this.command);
    localStorage.setItem(
      localStorageKeys.eventQueryCommand,
      JSON.stringify(this.command)
    );

    this.customEventAccordion &&
      this.customEventAccordion.setFilter(this.editorChoiceText);
    // this.eventDateAccordion && this.eventDateAccordion.clearFilter(null, false);
    this.mode === 'topbar' && this.closeAccordion();

    if (this.mode !== 'map') {
      this.router.navigateByUrl(
        getRouteUrl(routeConfigs.visitor.eventCalendar, {
          hubName: this.hubName,
        }),
        { skipLocationChange: this.noChangeUrlWhenRedirect }
      );
    }
  }

  public closeAccordion() {
    this.eventDateAccordion && this.eventDateAccordion.close();
    this.customEventAccordion && this.customEventAccordion.close();
  }

  public editorChoiceTextHandler() {
    this.editorChoiceStore.infoCommand
      .takeUntil(this.isDestroyed)
      .subscribe((editorChoice: EditorChoiceDataSource) => {
        this.editorChoiceText = editorChoice.editorChoiceForEvent;
      });
  }

  public setSelectedCustomEventBtn(customEventBtnId) {
    console.log('customEventBtnId in event filter', customEventBtnId);

    setTimeout(() => {
      if (customEventBtnId) {
        const customBtn = this.customEvents.find(
          (x) => x._id.toString() === customEventBtnId
        );
        if (customBtn) {
          this.selectedCustomEvent = customBtn;
          this.customEventAccordion.setFilter(customBtn.btnText);
        }
      }
    });
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/visitor/event.filter/event.filter.component.ts